import { useMemo, useState, type ReactNode } from 'react';
import { JsonEditor } from '../components/JsonEditor';
import { PageHeader } from '../components/PageHeader';
import { newEntry } from '../components/forms/defaults';
import { IssueList } from '../components/forms/fields';
import { ContentRefsContext } from '../components/forms/refs';
import { collectionForms, sectionForms } from '../components/forms/registry';
import {
  collections,
  idKeyOf,
  isNumericId,
  isWide,
  sections,
  useEnsureDraft,
  type CollectionKey,
  type Entry,
  type SectionKey,
} from '../lib/contentConfig';
import { draftStore, type Bundle } from '../lib/draft';
import { teamsSectionDefaults } from '../lib/teamsDefaults';
import { issuesFor, validateBundle, type Issue } from '../lib/validate';

const btn =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/50 disabled:opacity-40';

type Mode = 'form' | 'json';

/** The Form / Edit JSON switch. Without a form, only the JSON editor is offered. */
function ModeTabs({
  mode,
  setMode,
  hasForm,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
  hasForm: boolean;
}) {
  if (!hasForm) return <span className="text-xs text-bruise">No form yet: edit as JSON.</span>;
  const tab = (m: Mode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === m}
      onClick={() => setMode(m)}
      className={`${btn} ${mode === m ? 'bg-ink text-newsprint' : ''}`}
    >
      {label}
    </button>
  );
  return (
    <div role="tablist" className="flex gap-1">
      {tab('form', 'Form')}
      {tab('json', 'Edit JSON')}
    </div>
  );
}

function IssueSummary({ issues }: { issues: Issue[] }) {
  const errors = issues.filter((i) => i.severity === 'error').length;
  const warnings = issues.length - errors;
  if (!issues.length) return <span className="text-xs text-benday">No problems found.</span>;
  return (
    <span className="text-xs">
      {errors > 0 && <span className="font-semibold text-blam">{errors} error(s)</span>}
      {errors > 0 && warnings > 0 && ' · '}
      {warnings > 0 && <span className="text-bruise">{warnings} warning(s)</span>}
    </span>
  );
}

function LoadState({ loading, error }: { loading: boolean; error: unknown }) {
  if (error) return <p className="text-blam">Could not load content: {String(error)}</p>;
  return loading ? <p>Loading content&hellip;</p> : null;
}

/** `extra` renders under the header with the draft in context (the vehicles page's ladder). */
export function CollectionEditorPage({
  collection,
  extra,
}: {
  collection: CollectionKey;
  extra?: ReactNode;
}) {
  const { draft, loading, error } = useEnsureDraft();
  const config = collections[collection];
  const Form = collectionForms[collection];
  const [picked, setSelected] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>(Form ? 'form' : 'json');
  const issues = useMemo(() => (draft ? validateBundle(draft.bundle) : []), [draft]);
  const idKey = idKeyOf(collection);
  // `?id=` (a click-through from the content tree) opens that entry until another is picked.
  const [wanted] = useState(() => new URLSearchParams(window.location.search).get('id'));
  const numeric = isNumericId(collection);
  const idOf = (e: Entry | undefined) => String((e?.[idKey] as string | number | undefined) ?? '');

  if (loading || error || !draft) return <LoadState loading={loading || !draft} error={error} />;

  const entries = (draft.bundle[collection] as unknown as Entry[] | undefined) ?? [];
  const wantedAt = wanted ? entries.findIndex((e) => idOf(e) === wanted) : -1;
  const selected = picked ?? (wantedAt >= 0 ? wantedAt : null);
  const entry = selected === null ? undefined : entries[selected];
  const sectionIssues = issues.filter((i) => i.section === collection);
  const replace = (list: Entry[]) =>
    draftStore.update((b) => ({ ...b, [collection]: list }) as unknown as Bundle);
  const setAt = (i: number, next: Entry) => replace(entries.map((e, j) => (j === i ? next : e)));

  const uniqueId = (base: string): string => {
    if (numeric) {
      // Tech tiers are keyed by their number: the next one above every tier in use.
      const used = entries.map((e) => Number(e[idKey] ?? 0)).filter(Number.isFinite);
      return String(used.length ? Math.max(...used) + 1 : 1);
    }
    const ids = new Set(entries.map(idOf));
    let n = entries.length + 1;
    while (ids.has(`${base}_${n}`)) n++;
    return `${base}_${n}`;
  };
  const add = () => {
    replace([...entries, newEntry[collection](uniqueId(`new_${collection}`))]);
    setSelected(entries.length);
  };
  const duplicate = (i: number) => {
    const copy = structuredClone(entries[i]!);
    const nextId = uniqueId(`${idOf(copy) || 'copy'}_copy`);
    replace([...entries, { ...copy, [idKey]: numeric ? Number(nextId) : nextId }]);
    setSelected(entries.length);
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= entries.length) return;
    const next = [...entries];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x!);
    replace(next);
    setSelected(to);
  };
  const remove = (i: number) => {
    if (!confirm(`Delete "${idOf(entries[i])}" from the draft?`)) return;
    replace(entries.filter((_, j) => j !== i));
    setSelected(null);
  };

  return (
    <ContentRefsContext.Provider value={draft.bundle}>
      <PageHeader title={config.title} description={config.description}>
        <div className="flex flex-col items-end gap-1">
          <button type="button" onClick={add} className={btn}>
            Add
          </button>
          <IssueSummary issues={sectionIssues} />
        </div>
      </PageHeader>
      {extra}
      <div
        className={`grid gap-4 ${isWide(collection) ? 'xl:grid-cols-[minmax(0,0.45fr)_minmax(0,2fr)]' : 'lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)]'}`}
      >
        <div>
          <table className="w-full border-2 border-ink bg-white text-sm">
            <thead className="bg-ink text-newsprint">
              <tr>
                <th className="px-2 py-1 text-left">{idKey === 'id' ? 'Id' : 'Key'}</th>
                {config.columns.map(([label]) => (
                  <th key={label} className="px-2 py-1 text-left">
                    {label}
                  </th>
                ))}
                <th className="px-2 py-1" aria-label="Problems" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const rowIssues = sectionIssues.filter((x) => x.index === i);
                const errs = rowIssues.filter((x) => x.severity === 'error').length;
                return (
                  <tr
                    key={i}
                    onClick={() => setSelected(i)}
                    className={`cursor-pointer border-t border-newsprint ${i === selected ? 'bg-benday/30' : 'hover:bg-caption/20'}`}
                  >
                    <td className="px-2 py-1 font-mono">{idOf(e)}</td>
                    {config.columns.map(([label, get]) => (
                      <td key={label} className="px-2 py-1">
                        {get(e)}
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right text-xs">
                      {errs > 0 ? (
                        <span className="font-semibold text-blam" title={`${errs} error(s)`}>
                          ● {errs}
                        </span>
                      ) : rowIssues.length > 0 ? (
                        <span className="text-bruise" title={`${rowIssues.length} warning(s)`}>
                          ○ {rowIssues.length}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-bruise">
            Order matters for categories (first matching tag wins). Changes stay in this
            browser&apos;s draft until you publish.
          </p>
        </div>
        <div>
          {entry && selected !== null ? (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-mono text-lg font-bold">{idOf(entry) || '(no id)'}</h2>
                <ModeTabs mode={mode} setMode={setMode} hasForm={!!Form} />
              </div>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={btn}
                  disabled={selected === 0}
                  onClick={() => move(selected, selected - 1)}
                >
                  Move up
                </button>
                <button
                  type="button"
                  className={btn}
                  disabled={selected === entries.length - 1}
                  onClick={() => move(selected, selected + 1)}
                >
                  Move down
                </button>
                <button type="button" className={btn} onClick={() => duplicate(selected)}>
                  Duplicate
                </button>
                <button
                  type="button"
                  className={`${btn} text-blam`}
                  onClick={() => remove(selected)}
                >
                  Delete
                </button>
              </div>
              {Form && mode === 'form' ? (
                <>
                  <EntryIssues issues={sectionIssues.filter((x) => x.index === selected)} />
                  <Form
                    value={entry}
                    errors={issuesFor(issues, collection, selected)}
                    onChange={(next: Entry) => setAt(selected, next)}
                  />
                </>
              ) : (
                <>
                  <EntryIssues issues={sectionIssues.filter((x) => x.index === selected)} />
                  <JsonEditor
                    key={`${collection}-${selected}`}
                    label="Entry JSON"
                    value={entry}
                    onApply={(parsed) => {
                      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
                        return 'An entry is a JSON object.';
                      const next = parsed as Entry;
                      if (!next[idKey] && !(numeric && next[idKey] === 0))
                        return `Every entry needs a "${idKey}".`;
                      setAt(selected, next);
                      return null;
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <p className="text-sm text-bruise">
              Pick an entry to edit it. Changes stay in the draft until you publish.
            </p>
          )}
        </div>
      </div>
    </ContentRefsContext.Provider>
  );
}

/** Every issue for the entry, at the top of the editor, so nothing hides in a collapsed list. */
function EntryIssues({ issues }: { issues: Issue[] }) {
  if (!issues.length) return null;
  return (
    <div className="mb-3 rounded border-2 border-blam/60 bg-white p-2">
      <p className="text-xs font-semibold">Problems in this entry</p>
      <IssueList issues={issues} />
    </div>
  );
}

export function SectionEditorPage({ section }: { section: SectionKey }) {
  const { draft, loading, error } = useEnsureDraft();
  const config = sections[section];
  const Form = sectionForms[section];
  const [mode, setMode] = useState<Mode>(Form ? 'form' : 'json');
  const issues = useMemo(() => (draft ? validateBundle(draft.bundle) : []), [draft]);

  if (loading || error || !draft) return <LoadState loading={loading || !draft} error={error} />;

  const sectionIssues = issues.filter((i) => i.section === section);
  // A draft from a bundle published before a section existed may not have it: show what the server would use (the
  // C# defaults for the Teams on the Map blocks, else an empty object). The draft changes only when applied.
  const absent = draft.bundle[section] == null;
  const defaults = (teamsSectionDefaults as Partial<Record<SectionKey, () => object>>)[section];
  const value = draft.bundle[section] ?? defaults?.() ?? {};
  const apply = (next: unknown) => draftStore.update((b) => ({ ...b, [section]: next }) as Bundle);

  return (
    <ContentRefsContext.Provider value={draft.bundle}>
      <PageHeader title={config.title} description={config.description}>
        <div className="flex flex-col items-end gap-1">
          <ModeTabs mode={mode} setMode={setMode} hasForm={!!Form} />
          <IssueSummary issues={sectionIssues} />
        </div>
      </PageHeader>
      <EntryIssues issues={sectionIssues} />
      {absent && (
        <p className="mb-3 text-xs text-bruise">
          This draft doesn&apos;t have this section yet. Showing{' '}
          {defaults ? 'the defaults the server uses' : 'an empty section'}; apply to add it.
        </p>
      )}
      {Form && mode === 'form' ? (
        <Form value={value} errors={issuesFor(issues, section)} onChange={apply} />
      ) : (
        <JsonEditor
          key={section}
          label={`${config.title} JSON`}
          value={value}
          onApply={(parsed) => {
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
              return 'This section is a JSON object.';
            apply(parsed);
            return null;
          }}
        />
      )}
    </ContentRefsContext.Provider>
  );
}
